/*
 * A PDF viewer that scrolls.
 *
 * The viewer this replaces (angular-pdf's <ng-pdf>) draws one page onto one canvas and moves between
 * pages with next/previous buttons. That is not how anyone reads a document: a processing report or a
 * referral letter is read top to bottom, and paging through it a canvas at a time loses your place.
 * So every page is laid out in one scrolling column, the way a PDF reads in a browser's own viewer.
 *
 * Pages are drawn as they come into view rather than all at once - a document is opened to read the
 * first page of it, and rendering thirty pages up front would hold the modal blank while it worked.
 *
 * Drops into the same place <ng-pdf> did: it reads the URL off the scope the modal was opened with,
 * takes the same optional httpHeaders (for documents fetched from our own API rather than a blob),
 * and calls the same on-load / on-error callbacks.
 */
app.directive("pdfScrollViewer", ["$window", "$timeout", "$log", function ($window, $timeout, $log) {

	// A PDF measures itself in points, 72 to the inch. Browsers reckon 96 pixels to the inch, so a page
	// drawn at 96/72 comes out the size the page actually is - an A4 page is A4 on screen, and a report
	// printed and held beside the screen matches it. That is the size the document opens at, rather than
	// whatever scale happened to make it fill the panel: a page that resizes itself to the window is a
	// page whose text changes size every time the window does.
	//
	// This is as close to the real thing as a browser will say. The physical size of the display is not
	// exposed to a page; 96 pixels to the inch is the reference every browser reckons CSS lengths against
	// and what it uses for its own "actual size", and the display's true pixel density is then accounted
	// for separately when the page is drawn, so the page is sharp as well as the right size.
	var ACTUAL_SIZE = 96 / 72;

	var ZOOM_STEP = 0.2;
	var MIN_SCALE = 0.25;
	var MAX_SCALE = 4;

	// Pages within this much of the viewport - measured in viewport heights either side - are drawn
	// before they are reached, so a steady scroll stays ahead of the rendering rather than waiting on it.
	var RENDER_MARGIN = 1;

	// Scroll fires far faster than there is any point re-checking what is visible.
	var SCROLL_THROTTLE = 60;

	return {
		restrict: "E",
		scope: {
			pdfUrl: "=",
			httpHeaders: "=?",
			onLoad: "&?",
			onError: "&?"
		},
		template: [
			'<div class="pdf-scroll-viewer">',
			'	<nav class="pdf-scroll-viewer-toolbar">',
			'		<button type="button" id="pdf_zoom_in" class="btn btn-navy font-medium" ng-click="zoomIn()" ng-disabled="!pageCount">+</button>',
			'		<button type="button" id="pdf_zoom_out" class="btn btn-navy font-medium" ng-click="zoomOut()" ng-disabled="!pageCount">-</button>',
			'		<label class="control-label" ng-show="pageCount">Page {{pageNum}} of {{pageCount}}</label>',
			'	</nav>',
			'	<div class="pdf-scroll-viewer-scroll">',
			'		<div class="pdf-scroll-viewer-message" ng-if="!pageCount">{{message}}</div>',
			'		<div class="pdf-scroll-viewer-pages"></div>',
			'	</div>',
			'</div>'
		].join(""),
		link: function (scope, element) {
			var scrollEl = element[0].querySelector(".pdf-scroll-viewer-scroll");
			var pagesEl = angular.element(element[0].querySelector(".pdf-scroll-viewer-pages"));

			var pageViews = [];
			var pdfDoc = null;
			var loaderTask = null;

			var scale = ACTUAL_SIZE;

			// A modal can be closed and reopened on another document before the first one has finished
			// loading, and zoom can be pressed while pages are still drawing. Anything in flight checks
			// its token against the current one before it touches the DOM, so late work from a document
			// or a scale that is no longer on screen is dropped rather than drawn over what is.
			var loadToken = 0;
			var layoutToken = 0;

			var scrollTimer = null;
			var settleTimer = null;

			scope.pageNum = 0;
			scope.pageCount = 0;
			scope.message = "Loading document...";

			// Canvases are sized in CSS pixels but drawn at the screen's real pixel density - a page
			// rendered at 1x on a retina display is legible but visibly soft, and these are documents
			// people read fine print off.
			function backingRatio(canvas) {
				var ctx = canvas.getContext("2d");
				var dpr = $window.devicePixelRatio || 1;
				var bsr = ctx.webkitBackingStorePixelRatio || ctx.mozBackingStorePixelRatio
					|| ctx.msBackingStorePixelRatio || ctx.oBackingStorePixelRatio
					|| ctx.backingStorePixelRatio || 1;
				return dpr / bsr;
			}

			function cancelRender(view) {
				if (!view.renderTask) return;
				try {
					view.renderTask.cancel();
				} catch (e) {
					// A task that has already finished has nothing to cancel.
				}
				view.renderTask = null;
			}

			function createPageView(page) {
				var container = $window.document.createElement("div");
				container.className = "pdf-scroll-viewer-page";
				pagesEl.append(container);

				var view = { page: page, container: container, canvas: null, renderTask: null, rendered: false };
				// Sized before it is drawn, so the column stands at its true height from the first frame
				// and the scrollbar means what it says while the pages are still filling in.
				applyScale(view);
				return view;
			}

			// Each render gets a canvas of its own rather than painting over the last one. PDF.js refuses
			// to render twice onto the same canvas and only forgives it once the first render has fully
			// unwound - which a cancelled one does not always do - so a page zoomed while it was still
			// drawing could be left permanently blank. A new canvas cannot collide with a render that is
			// still winding down: it draws into the old one, which is already detached and discarded.
			function freshCanvas(view) {
				var canvas = $window.document.createElement("canvas");
				if (view.canvas) {
					view.container.replaceChild(canvas, view.canvas);
				} else {
					view.container.appendChild(canvas);
				}
				view.canvas = canvas;
				return canvas;
			}

			// Sizes a page's canvas for the current scale, on a canvas that has nothing drawn on it yet.
			function applyScale(view) {
				var canvas = freshCanvas(view);
				canvas.width = 0;
				canvas.height = 0;

				var viewport = view.page.getViewport(scale);
				canvas.style.width = Math.floor(viewport.width) + "px";
				canvas.style.height = Math.floor(viewport.height) + "px";
				view.rendered = false;
			}

			function renderPage(view) {
				if (view.rendered || view.renderTask) return;

				var token = layoutToken;
				var viewport = view.page.getViewport(scale);
				var ratio = backingRatio(view.canvas);

				view.canvas.width = Math.floor(viewport.width * ratio);
				view.canvas.height = Math.floor(viewport.height * ratio);

				var ctx = view.canvas.getContext("2d");

				// The device-pixel scale is handed to PDF.js to apply rather than set on the context here.
				// PDF.js keeps its own copy of the canvas transform - it wraps the context on the way in and
				// starts that copy at identity - and positions transparency groups, soft masks, images and
				// shadings from it rather than from the canvas. A scale applied behind its back leaves the
				// copy short by the pixel ratio, and everything placed from it lands at the wrong size in
				// the wrong corner: on a 2x display the affected parts of a page are drawn half-size in its
				// top-left. Handed over as the render's own transform, the scale goes on through PDF.js's
				// transform call and its bookkeeping stays true - and on a 1x display, where the ratio is
				// 1 and there is nothing to apply, none of this arises, which is why it survived a look.
				view.renderTask = view.page.render({
					canvasContext: ctx,
					viewport: viewport,
					transform: ratio === 1 ? null : [ratio, 0, 0, ratio, 0, 0]
				});
				view.renderTask.promise.then(function () {
					view.renderTask = null;
					if (token === layoutToken) view.rendered = true;
				}, function (reason) {
					view.renderTask = null;
					// A cancelled render is the normal result of zooming or closing mid-draw, not a fault.
					if (token === layoutToken) $log.log(reason);
				});
			}

			function renderVisible() {
				if (!pageViews.length) return;

				var top = scrollEl.scrollTop;
				var height = scrollEl.clientHeight;
				var margin = height * RENDER_MARGIN;

				pageViews.forEach(function (view) {
					var pageTop = view.container.offsetTop;
					var pageBottom = pageTop + view.container.offsetHeight;
					if (pageBottom < top - margin || pageTop > top + height + margin) return;
					renderPage(view);
				});
			}

			// The page number now reads out where the scroll is rather than driving it. Measured a third
			// of the way down the viewport, not at the very top, so the number turns over when the next
			// page is the one being read rather than the moment its top edge appears.
			function updateCurrentPage() {
				if (!pageViews.length) return;

				var mark = scrollEl.scrollTop + (scrollEl.clientHeight / 3);
				var current = 1;

				for (var i = 0; i < pageViews.length; i++) {
					if (pageViews[i].container.offsetTop <= mark) current = i + 1;
				}

				if (current !== scope.pageNum) {
					scope.pageNum = current;
					if (!scope.$root.$$phase) scope.$applyAsync();
				}
			}

			// Sizes every page for the current scale and drops what was drawn at the old one. Called on
			// zoom and on resize, where the pages on screen are still the right pages at the wrong size.
			function layout() {
				layoutToken++;

				pageViews.forEach(function (view) {
					cancelRender(view);
					// The old canvas goes with the old scale rather than being stretched to the new size,
					// which would read as a broken page until the fresh render landed.
					applyScale(view);
				});

				renderVisible();
				updateCurrentPage();
			}

			function buildPages(token) {
				var numbers = [];
				for (var i = 1; i <= pdfDoc.numPages; i++) numbers.push(i);

				// Every page is asked for up front, but only for its dimensions - that is metadata, not
				// a render. Laying the whole column out at its true height straight away is what makes
				// the scrollbar honest from the first frame.
				$window.Promise.all(numbers.map(function (n) { return pdfDoc.getPage(n); }))
					.then(function (pages) {
						if (token !== loadToken) return;
						if (!pages.length) {
							failed(new Error("The document has no pages."));
							return;
						}

						pageViews = pages.map(createPageView);

						scope.pageCount = pages.length;
						scope.pageNum = 1;
						scope.message = "";
						if (!scope.$root.$$phase) scope.$applyAsync();

						renderVisible();

						// Asked again a beat later. The modal is still animating in at this point, so how
						// much of the document is on screen is not yet how much of it will be - without
						// this the pages below the fold of a half-open panel are never asked for.
						settleTimer = $timeout(function () {
							settleTimer = null;
							if (token !== loadToken) return;
							renderVisible();
							updateCurrentPage();
						}, 400);
					}, function (error) {
						if (token !== loadToken) return;
						failed(error);
					});
			}

			function failed(error) {
				$log.log(error);
				scope.pageCount = 0;
				scope.message = "This document could not be displayed.";
				if (!scope.$root.$$phase) scope.$applyAsync();
				if (angular.isFunction(scope.onError)) scope.onError({ error: error });
			}

			function reset() {
				loadToken++;
				layoutToken++;

				if (settleTimer) {
					$timeout.cancel(settleTimer);
					settleTimer = null;
				}

				pageViews.forEach(cancelRender);
				pageViews = [];
				pagesEl.empty();

				scope.pageCount = 0;
				scope.pageNum = 0;
				scrollEl.scrollTop = 0;

				if (loaderTask) {
					var closing = loaderTask;
					loaderTask = null;
					try {
						var closed = closing.destroy();
						// Tearing down mid-load rejects whatever was still in flight, which is the point
						// of tearing it down - swallowed so it does not surface as an unhandled rejection.
						if (closed && angular.isFunction(closed.catch)) closed.catch(function () { });
					} catch (e) {
						// Nothing to tear down if it never got going.
					}
				}
				pdfDoc = null;
			}

			function load(url) {
				reset();

				scope.message = "Loading document...";

				// The modals open before the document has been fetched, so an empty URL is the normal
				// first state rather than a mistake - it is the wait, and it is said out loud.
				if (!url) return;

				var token = loadToken;
				var params = { url: url };
				if (scope.httpHeaders) params.httpHeaders = scope.httpHeaders;

				// The worker script is loaded into the page itself rather than served separately, which
				// is what the rest of the application has always done here.
				PDFJS.disableWorker = true;

				loaderTask = PDFJS.getDocument(params);
				var docPromise = loaderTask.promise || loaderTask;

				docPromise.then(function (doc) {
					if (token !== loadToken) return;

					pdfDoc = doc;
					if (angular.isFunction(scope.onLoad)) scope.onLoad();
					buildPages(token);
				}, function (error) {
					if (token !== loadToken) return;
					failed(error);
				});
			}

			function clampScale(value) {
				return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
			}

			// Zooming keeps the reader where they were: the page they are on is scrolled back to,
			// rather than the column growing underneath them and taking their place with it.
			function rescale(next) {
				if (!pageViews.length) return;

				var anchor = scope.pageNum;
				scale = clampScale(next);
				layout();

				var view = pageViews[anchor - 1];
				if (view) scrollEl.scrollTop = view.container.offsetTop;
			}

			scope.zoomIn = function () {
				rescale(scale + ZOOM_STEP);
			};

			scope.zoomOut = function () {
				rescale(scale - ZOOM_STEP);
			};

			angular.element(scrollEl).on("scroll", function () {
				if (scrollTimer) return;
				// Deliberately outside a digest: scrolling would otherwise dirty-check the whole
				// application every 60ms. updateCurrentPage asks for one only when the number changes.
				scrollTimer = $timeout(function () {
					scrollTimer = null;
					renderVisible();
					updateCurrentPage();
				}, SCROLL_THROTTLE, false);
			});

			// Nothing is re-laid-out when the window resizes: the page is the size it is, and a resized
			// window shows more or less of it rather than changing it. Only how much is visible changes,
			// which is the scroll handler's business.
			scope.$watch("pdfUrl", function (url, previous) {
				if (url === previous && scope.pageCount) return;
				scale = ACTUAL_SIZE;
				load(url);
			});

			scope.$on("$destroy", function () {
				if (scrollTimer) $timeout.cancel(scrollTimer);
				if (settleTimer) $timeout.cancel(settleTimer);
				reset();
			});
		}
	};
}]);
